import type { BarAsset } from "../types/bar-asset";
import type { StrandLoss } from "../types/strand-loss";
import { SPEED_BAR_BOTTOM_INSET, SPEED_BAR_HEIGHT } from "./bar-helper";

export interface StrandLossOverlaySegment {
  id: number;
  loss: StrandLoss;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface OverlayViewport {
  start: Date;
  width: number;
  toX: (date: Date) => number;
}

const STRAND_CODES = new Set([1, 2, 3, 4, 5]);
const MINIMUM_OVERLAY_WIDTH = 1;

export const mapStrandLossOverlays = (
  losses: StrandLoss[],
  assets: BarAsset[],
  viewport: OverlayViewport,
): StrandLossOverlaySegment[] => {
  const caster = assets.find((asset) => asset.name === "Caster");
  if (!caster || viewport.width <= 0) return [];

  return losses.flatMap((loss) => {
    if (!STRAND_CODES.has(loss.Strand)) return [];

    // The default speed placeholder also owns the lane when no speed event exists.
    const lane = caster.barTasks.find(
      (task) =>
        task.type === "taskLine" &&
        task.source.eventCode === String(loss.Strand),
    );
    if (!lane) return [];

    const start = Math.max(loss.Start.getTime(), viewport.start.getTime());
    const end = loss.End.getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return [];
    }

    const x = Math.max(0, viewport.toX(new Date(start)));
    const right = Math.max(
      0,
      Math.min(viewport.width, viewport.toX(new Date(end))),
    );
    if (
      !Number.isFinite(x) ||
      !Number.isFinite(right) ||
      x >= viewport.width ||
      right < x
    )
      return [];

    // A positive interval narrower than a pixel still needs a visible warning.
    return [
      {
        id: loss.DelayId,
        loss,
        x,
        y: lane.y + lane.height - SPEED_BAR_BOTTOM_INSET,
        width: Math.min(
          viewport.width - x,
          Math.max(MINIMUM_OVERLAY_WIDTH, right - x),
        ),
        height: SPEED_BAR_HEIGHT,
      },
    ];
  });
};
